import { useCallback, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import routes from '../../../routes/paths'
import { CareTabs, Specialty } from '../data'
import { DirectoryQueryParam } from '../interfaces'

type HandleRedirectFunc = (
  route: string,
  queryParams: DirectoryQueryParam[]
) => void

export const useFindCareSearchInput = () => {
  const history = useHistory()

  const [selectedTab, setSelectedTab] = useState<CareTabs>(CareTabs.ALL)

  const [inputText, setInputText] = useState('')
  const selectInputRef = useRef<any>()

  const [enableProvidersFetch, setEnableProvidersFetch] =
    useState<boolean>(true)
  const typingTimeout = useRef<any>()

  const handleInputChange = useCallback((inputText: string, event: any) => {
    if (event.action === 'input-change') {
      // Allow refetch of providers search endpoint 1 second after the user stops typing
      clearTimeout(typingTimeout.current)
      setEnableProvidersFetch(false)
      typingTimeout.current = setTimeout(() => {
        setEnableProvidersFetch(true)
      }, 1000)

      setInputText(inputText)
    }
  }, [])

  const onClearSelect = () => {
    setInputText('')
  }

  const handleSpecialtyClick =
    (handleRedirect: HandleRedirectFunc) => (specialty: Specialty) => {
      const route =
        specialty.type === 'facility'
          ? routes.facilitiesDirectory
          : routes.providersDirectory

      const filterType = specialty.type === 'facility' ? 'service' : 'specialty'

      const queryParams: DirectoryQueryParam[] = [
        { key: filterType, value: specialty.value || specialty.name }
      ]

      // Will select all facility services that contain the value of the clicked common specialty in the Facility map page
      if (specialty.type === 'facility') {
        queryParams.push({
          key: 'common',
          value: true
        })
      }

      handleRedirect(route, queryParams)
    }

  const onSelectTab = (i: any) => {
    setSelectedTab(Object.values(CareTabs)[i])
    if (inputText) selectInputRef.current?.select?.focus()
  }

  const handleSelection =
    (handleRedirect: HandleRedirectFunc) => (selected: any) => {
      if (selected === null) {
        onClearSelect()
      } else {
        if (selected.type === 'facility' || selected.type === 'provider') {
          const route =
            selected.type === 'facility'
              ? routes.facilitiesDirectory
              : routes.providersDirectory
          history.push(`${route}/${selected.id}`)
        }

        if (selected.type === 'specialty' || selected.type === 'service') {
          const route =
            selected.type === 'service'
              ? routes.facilitiesDirectory
              : routes.providersDirectory

          handleRedirect(route, [{ key: selected.type, value: selected.value }])
        }

        setInputText(selected.label)
      }
    }

  const placeholder =
    selectedTab === CareTabs.ALL
      ? 'Search Providers & Facilities'
      : selectedTab === CareTabs.PROVIDER
      ? 'Search Providers'
      : 'Search Facilities'

  return {
    selectedTab,
    inputText,
    handleSelection,
    onSelectTab,
    handleSpecialtyClick,
    handleInputChange,
    enableProvidersFetch,
    onClearSelect,
    selectInputRef,
    placeholder
  }
}
